/**
 * 同じ名前・同じ中身のファイルが既にあるときの確認。
 * 取り込みを始める **前** に出す。勝手に消したり上書きしたりはしない。
 *
 * 選べること（ファイルごと／まとめて）:
 *   既存を開く ／ 既存をこのフォルダへ移動 ／ 別の書類として登録（理由が必要） ／ スキップ
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const CHOICES = [
  ['open_existing', '既存を開く（登録しない）'],
  ['move_existing', '既存をこのフォルダへ移動'],
  ['separate', '別の書類として登録'],
  ['skip', 'スキップ'],
];

export default function DuplicateCheckDialog({ open, result, folderName, onCancel, onProceed }) {
  const rows = (result?.rows || []).filter((r) => r.duplicates?.length);
  const [choice, setChoice] = useState({});
  const [reason, setReason] = useState({});
  const [all, setAll] = useState('');

  if (!open || !rows.length) return null;
  const pick = (name) => choice[name] || all || 'separate';
  const needsReason = rows.some((r) => pick(r.filename) === 'separate' && !(reason[r.filename] || '').trim());

  const apply = () => onProceed(rows.map((r) => ({
    filename: r.filename,
    choice: pick(r.filename),
    reason: (reason[r.filename] || '').trim(),
    existing: r.duplicates[0],
  })));

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-2xl" aria-describedby={undefined}
        data-testid="dms-dup-dialog">
        <DialogHeader>
          <DialogTitle>同じファイルが既にあります（{rows.length}件）</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500">
          {result?.notice_ja}
          {folderName && <span className="ml-1">取り込み先: {folderName}</span>}
        </p>

        {rows.length > 1 && (
          <div className="flex items-center gap-2 text-xs" data-testid="dms-dup-all">
            <span className="font-semibold text-zinc-700">すべて同じ操作:</span>
            <select value={all} onChange={(e) => setAll(e.target.value)}
              className="h-8 rounded-md border border-zinc-200 px-2 text-xs"
              data-testid="dms-dup-all-select">
              <option value="">個別に選ぶ</option>
              {CHOICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        )}

        <div className="max-h-72 space-y-3 overflow-auto">
          {rows.map((r) => (
            <div key={r.filename} className="rounded-lg border border-zinc-200 p-3"
              data-testid={`dms-dup-row-${r.filename}`}>
              <p className="truncate text-sm font-semibold text-zinc-900">{r.filename}</p>
              <ul className="mt-1 space-y-1">
                {r.duplicates.map((d) => (
                  <li key={d.document_id} className="text-[11px] leading-5 text-zinc-600">
                    <span className="font-mono text-zinc-500">{d.document_number || '—'}</span>
                    {' '}{d.title}
                    <span className="text-zinc-400">
                      {' ・'}{d.folder_name}
                      {' ・'}{(d.updated_at || d.created_at || '').slice(0, 10)}
                      {d.uploaded_by ? ` ・${d.uploaded_by}` : ''}
                    </span>
                    {d.same_file && (
                      <span className="ml-1 rounded border border-rose-200 bg-rose-50 px-1 text-[10px] font-semibold text-rose-700"
                        data-testid={`dms-dup-samefile-${d.document_id}`}>同一ファイル</span>
                    )}
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select value={pick(r.filename)} disabled={!!all}
                  onChange={(e) => setChoice((c) => ({ ...c, [r.filename]: e.target.value }))}
                  className="h-8 rounded-md border border-zinc-200 px-2 text-xs"
                  data-testid={`dms-dup-choice-${r.filename}`}>
                  {CHOICES.map(([v, l]) => {
                    const c = (r.choices || []).find((x) => x.value === v);
                    return <option key={v} value={v} disabled={c && c.enabled === false}>{l}</option>;
                  })}
                </select>
                {pick(r.filename) === 'separate' && (
                  <Input value={reason[r.filename] || ''}
                    onChange={(e) => setReason((s) => ({ ...s, [r.filename]: e.target.value }))}
                    placeholder="別書類にする理由（必須）" className="h-8 max-w-xs text-xs"
                    data-testid={`dms-dup-reason-${r.filename}`} />
                )}
                {(r.revision_candidates || []).length > 0 && (
                  <span className="text-[11px] text-zinc-500">
                    図面の改訂候補あり — 版として足す場合は既存を開いて「版を追加」から
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="dms-dup-cancel">
            取り込みをやめる
          </Button>
          <Button onClick={apply} disabled={needsReason}
            className="bg-teal-700 hover:bg-teal-800" data-testid="dms-dup-proceed">
            この内容で進める
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
